import { useRef, useState, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import { COMPANY_ID, faceRegisterSecret } from '../services/api';

// 会社ごとに顔データを分離する（別APKなら混在しないが、念のためキーにcompany_idを含める）
const STORAGE_KEY = `catms_face_descriptors_${COMPANY_ID}`;

// ─── AES-GCM 暗号化ユーティリティ ──────────────────────────────────────────
const FACE_ENC_KEY = 'CATMS_FACE_FEATURES_ENC';

async function getEncKey(): Promise<CryptoKey> {
  // デバイス固有キーを localStorage に永続保持する。
  // 顔特徴量は localStorage（永続）に暗号化保存されるため、鍵も同じ永続ストレージに
  // 置かないと、アプリ再起動（＝別セッション）で鍵が失われ復号できずログインできない。
  // （旧実装は sessionStorage 保持のため、登録できてもログイン時に必ず失敗していた）
  let rawKey = localStorage.getItem(FACE_ENC_KEY);
  if (!rawKey) {
    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
    const exported = await crypto.subtle.exportKey('raw', key);
    rawKey = btoa(String.fromCharCode(...new Uint8Array(exported)));
    localStorage.setItem(FACE_ENC_KEY, rawKey);
  }
  const keyData = Uint8Array.from(atob(rawKey), c => c.charCodeAt(0));
  return crypto.subtle.importKey('raw', keyData, 'AES-GCM', false, ['encrypt', 'decrypt']);
}

async function encryptFeatures(data: Float32Array): Promise<string> {
  const key = await getEncKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const buffer = data.buffer as ArrayBuffer;
  const enc = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, buffer);
  return btoa(String.fromCharCode(...iv)) + '.' + btoa(String.fromCharCode(...new Uint8Array(enc)));
}

async function decryptFeatures(encoded: string): Promise<Float32Array> {
  const [ivB64, dataB64] = encoded.split('.');
  const key = await getEncKey();
  const iv = Uint8Array.from(atob(ivB64), c => c.charCodeAt(0));
  const data = Uint8Array.from(atob(dataB64), c => c.charCodeAt(0));
  const dec = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);
  return new Float32Array(dec);
}
// ──────────────────────────────────────────────────────────────────────────────

/**
 * 顔認証モデルの配信元 URL。
 * APK も capacitor.config の server.url で本番サイトをリモート読み込みするため、
 * モデルはページと同一オリジン配下の /models から取得する（ブラウザ・APK 共通）。
 * 旧実装は Capacitor 環境で 'https://localhost/models' を返していたが、リモート
 * 読み込み構成では localhost にモデルが存在せず「AIモデル読み込み失敗」になっていた。
 * location.origin を明示的に前置してWebView上の相対パス解決の不安定さも回避する。
 */
function getModelUrl(): string {
  try {
    const origin = window.location.origin;
    if (origin && /^https?:/.test(origin)) return `${origin}/models`;
  } catch { /* ignore */ }
  return '/models';
}

interface StoredFace {
  userId: string;
  userName: string;
  descriptor?: number[];    // 旧形式（平文）。読み込みのみ後方互換。新規保存はしない
  encDescriptor?: string;   // 新形式（AES-GCM暗号化）
  faceSecret?: string;      // 顔認証ログイン用シークレット（サーバ発行）
  userRole?: string;        // オフラインログイン時のローカルセッション用
  companyId?: string;       // 同上
  pendingUpload?: boolean;  // オフライン中に登録し、サーバ未送信の特徴量がある
}

/**
 * サーバのロスター（全乗員のオフライン情報）を顔ストアへ反映する（共有端末向け）。
 * オフライン中でも任意の乗員へ顔で切り替えられるようにする。
 * この端末で本人登録した高精度な暗号化特徴量(encDescriptor)は保持し、
 * 未登録ユーザーにはサーバ特徴量を暗号化して入れる。secret/役割等は常に最新化。
 * 特徴量は個人識別符号に該当し得るため、平文のまま端末保存しない
 * (既存の旧形式(平文)エントリもこのタイミングで暗号化形式へ移行する)。
 */
export async function saveRosterFaces(faces: Array<{
  userId: string; userName: string; descriptor?: number[] | null;
  faceSecret?: string; userRole?: string; companyId?: string;
}>): Promise<void> {
  try {
    const stored: StoredFace[] = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    const byId = new Map<string, StoredFace>(stored.map(f => [f.userId, f]));
    // 旧形式(平文)で残っているエントリを暗号化形式へ移行
    for (const f of byId.values()) {
      if (!f.encDescriptor && f.descriptor && f.descriptor.length > 0) {
        f.encDescriptor = await encryptFeatures(new Float32Array(f.descriptor));
      }
      delete f.descriptor;
    }
    for (const f of faces) {
      const existing = byId.get(f.userId);
      if (existing) {
        if (f.userName) existing.userName = f.userName;
        if (f.faceSecret) existing.faceSecret = f.faceSecret;
        if (f.userRole) existing.userRole = f.userRole;
        if (f.companyId) existing.companyId = f.companyId;
        // ローカル登録済み(encDescriptor)は上書きしない。未保持のときのみサーバ特徴量を補完。
        if (!existing.encDescriptor && f.descriptor && f.descriptor.length > 0) {
          existing.encDescriptor = await encryptFeatures(new Float32Array(f.descriptor));
        }
        // サーバ側で顔データが削除されたユーザーは端末キャッシュも破棄する
        // （管理サイトの「顔認証データを削除」を端末へ反映）。
        // ただし本端末でオフライン登録した未送信分(pendingUpload)は消さない。
        if ((!f.descriptor || f.descriptor.length === 0) && existing.encDescriptor && !existing.pendingUpload) {
          delete existing.encDescriptor;
          delete existing.descriptor;
        }
      } else if (f.descriptor && f.descriptor.length > 0) {
        byId.set(f.userId, {
          userId: f.userId, userName: f.userName,
          encDescriptor: await encryptFeatures(new Float32Array(f.descriptor)),
          faceSecret: f.faceSecret, userRole: f.userRole, companyId: f.companyId,
        });
      }
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(byId.values())));
  } catch { /* 反映失敗は無視 */ }
}

/**
 * オフライン中に登録した顔特徴量（pendingUpload）をサーバへ再送する。
 * オンライン復帰時・起動時に App から呼ばれる。成功したら
 * サーバ発行のシークレットを保存しフラグを外す（=登録の完全な有効化）。
 */
export async function syncPendingFaceRegistration(userId: string): Promise<boolean> {
  try {
    const stored: StoredFace[] = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    const entry = stored.find(f => f.userId === userId);
    if (!entry?.pendingUpload || !entry.encDescriptor) return false;
    const descriptor = await decryptFeatures(entry.encDescriptor);
    const secret = await faceRegisterSecret(Array.from(descriptor));
    entry.faceSecret = secret;
    delete entry.pendingUpload;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    console.log('[FaceAuth] オフライン登録分の顔特徴量をサーバへ同期しました');
    return true;
  } catch (e) {
    console.warn('[FaceAuth] syncPendingFaceRegistration failed (still offline?):', e);
    return false;
  }
}

/** 端末に本人の顔データ（特徴量）が保存されているか（設定画面のオフライン時表示用） */
export function hasLocalFace(userId: string): boolean {
  try {
    const stored: StoredFace[] = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    const e = stored.find(f => f.userId === userId);
    return !!(e?.encDescriptor || (e?.descriptor && e.descriptor.length > 0));
  } catch { return false; }
}

/** 端末に保存された本人の顔データを削除する（サーバ側の削除と併せて呼ぶ） */
export function removeLocalFace(userId: string): void {
  try {
    const stored: StoredFace[] = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored.filter(f => f.userId !== userId)));
  } catch { /* ignore */ }
}

/** 保存済み顔データから、指定ユーザーの顔ログイン用シークレットを取得（同期時のトークン取得用） */
export function getStoredFaceSecret(userId: string): string | undefined {
  try {
    const stored: StoredFace[] = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return stored.find(f => f.userId === userId)?.faceSecret;
  } catch { return undefined; }
}

// face-api.js が内部で使う TF.js への参照
type TfLike = {
  ready(): Promise<void>;
  getBackend(): string;
  setBackend(b: string): Promise<boolean>;
  ENV: { flags: Record<string, unknown> };
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const tf: TfLike | undefined = (faceapi as any).tf;

/**
 * TensorFlow.js バックエンドの初期化。
 * WebGL(GPU) を優先する（CPU より数倍〜十倍速い）。以前は Capacitor で一律 CPU に
 * していたが、GPUの方が体感が大幅に良い。万一 WebGL が不正な特徴量(NaN等)を返す
 * 端末では、認証時に検出した特徴量を検証し CPU へ自動フォールバックする。
 */
let triedCpuFallback = false;

async function ensureTfBackend(): Promise<void> {
  if (!tf) {
    console.warn('[FaceAuth] tf not found in faceapi');
    return;
  }
  try {
    const current = tf.getBackend();
    if (!current) {
      // 明示的に WebGL を試す。失敗したら CPU。
      try { await tf.setBackend('webgl'); } catch { try { await tf.setBackend('cpu'); triedCpuFallback = true; } catch { /* ignore */ } }
    }
    await tf.ready();
    console.log('[FaceAuth] tf.ready() OK, backend:', tf.getBackend());
  } catch (e) {
    console.warn('[FaceAuth] ensureTfBackend failed:', e);
    // バックエンドが使えなくても続行（モデルロード時にエラーになる）
  }
}

/** WebGL で NaN 等の壊れた特徴量が出た端末向けに、一度だけ CPU へ切替える */
async function fallbackToCpu(): Promise<void> {
  if (!tf || triedCpuFallback) return;
  triedCpuFallback = true;
  try {
    await tf.setBackend('cpu');
    await tf.ready();
    console.warn('[FaceAuth] WebGLで不正な特徴量を検出→CPUバックエンドへフォールバック');
  } catch (e) { console.warn('[FaceAuth] cpu fallback failed:', e); }
}

/** 特徴量が有効か（NaN/Inf を含まず、ゼロベクトルでない） */
function isValidDescriptor(d: Float32Array | undefined | null): boolean {
  if (!d || d.length === 0) return false;
  let sumSq = 0;
  for (let i = 0; i < d.length; i++) {
    const v = d[i];
    if (!Number.isFinite(v)) return false;
    sumSq += v * v;
  }
  return sumSq > 1e-6;
}

/** GPUシェーダのJITを先に済ませ、初回認証の待ち時間を減らす（空フレームで3モデルすべてを起動） */
let warmedUp = false;
async function warmup(): Promise<void> {
  if (warmedUp) return;
  const c = document.createElement('canvas');
  c.width = 160; c.height = 160;
  try {
    await faceapi.detectSingleFace(c, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 }));
  } catch { /* ウォームアップ失敗は無視 */ }
  // 空キャンバスでは顔が見つからず withFaceLandmarks/withFaceDescriptor の連鎖が走らないため、
  // 特徴点・顔認識モデルはネットを直接空撃ちする。これをしないと一番重い顔認識モデルの
  // シェーダコンパイルが「実際の初回認証中」に走り、廉価端末で数秒待たされる。
  try { await faceapi.nets.faceLandmark68Net.detectLandmarks(c); } catch { /* 無視 */ }
  try { await faceapi.nets.faceRecognitionNet.computeFaceDescriptor(c); } catch { /* 無視 */ }
  warmedUp = true;
}

/** モデルロード（冪等。多重呼び出しは同一Promiseへ合流し二重ダウンロードを防ぐ）*/
let loadModelsInFlight: Promise<void> | null = null;

function loadModels(): Promise<void> {
  if (
    faceapi.nets.tinyFaceDetector.isLoaded &&
    faceapi.nets.faceLandmark68Net.isLoaded &&
    faceapi.nets.faceRecognitionNet.isLoaded
  ) return Promise.resolve();
  if (!loadModelsInFlight) {
    loadModelsInFlight = doLoadModels().finally(() => { loadModelsInFlight = null; });
  }
  return loadModelsInFlight;
}

async function doLoadModels(): Promise<void> {
  const modelUrl = getModelUrl();
  console.log('[FaceAuth] loadModels from:', modelUrl);

  // TF バックエンドを確実に初期化
  await ensureTfBackend();

  // まずマニフェストファイルにアクセスできるか確認（デバッグ用）
  try {
    const testUrl = `${modelUrl}/tiny_face_detector_model-weights_manifest.json`;
    const resp = await fetch(testUrl);
    console.log('[FaceAuth] manifest fetch status:', resp.status, 'ok:', resp.ok);
    if (!resp.ok) {
      throw new Error(`manifest fetch failed: HTTP ${resp.status} for ${testUrl}`);
    }
  } catch (fetchErr) {
    console.error('[FaceAuth] manifest pre-check failed:', fetchErr);
    throw fetchErr;
  }

  await Promise.all([
    faceapi.nets.tinyFaceDetector.loadFromUri(modelUrl),
    faceapi.nets.faceLandmark68Net.loadFromUri(modelUrl),
    faceapi.nets.faceRecognitionNet.loadFromUri(modelUrl),
  ]);

  console.log('[FaceAuth] models loaded OK');
}

/**
 * アプリ起動直後のプリロード。カメラ起動を待たずにモデル読込＋ウォームアップを
 * 裏で済ませ、ログイン画面に着いた時点で顔認証が即応答できる状態にする。
 * 初期描画と競合しないようアイドル時に開始し、失敗しても無視する
 * （startCamera / recognizeFace 側で再試行されるため安全）。
 */
export function preloadFaceAuth(): void {
  const run = () => { loadModels().then(() => warmup()).catch(() => { /* 起動時は無視 */ }); };
  type IdleWindow = Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
  const w = window as IdleWindow;
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(run, { timeout: 3000 });
  } else {
    setTimeout(run, 1000);
  }
}

/** videoが実際にフレームを持つまで最大3秒待つ */
function waitForVideoReady(video: HTMLVideoElement): Promise<void> {
  return new Promise(resolve => {
    if (video.videoWidth > 0 && video.readyState >= 2) { resolve(); return; }
    const interval = setInterval(() => {
      if (video.videoWidth > 0 && video.readyState >= 2) {
        clearInterval(interval);
        resolve();
      }
    }, 100);
    setTimeout(() => { clearInterval(interval); resolve(); }, 3000);
  });
}

/** video → canvas キャプチャ */
function captureFrame(video: HTMLVideoElement): HTMLCanvasElement {
  const w = video.videoWidth  || video.clientWidth  || 320;
  const h = video.videoHeight || video.clientHeight || 240;
  const canvas = document.createElement('canvas');
  canvas.width  = w;
  canvas.height = h;
  canvas.getContext('2d')?.drawImage(video, 0, 0, w, h);
  return canvas;
}

/** inputSize を絞ってリトライしながら顔検出（位置ガイドで事前に合わせている前提で軽量化） */
async function detectFace(source: HTMLCanvasElement) {
  const attempts = [
    { inputSize: 320 as const, scoreThreshold: 0.2 },
    { inputSize: 416 as const, scoreThreshold: 0.15 },
  ];
  for (const opt of attempts) {
    const result = await faceapi
      .detectSingleFace(source, new faceapi.TinyFaceDetectorOptions(opt))
      .withFaceLandmarks()
      .withFaceDescriptor();
    if (result) {
      if (isValidDescriptor(result.descriptor)) return result;
      // WebGL で壊れた特徴量 → CPU にフォールバックして同フレームで再計算
      if (!triedCpuFallback) {
        await fallbackToCpu();
        const retry = await faceapi
          .detectSingleFace(source, new faceapi.TinyFaceDetectorOptions(opt))
          .withFaceLandmarks()
          .withFaceDescriptor();
        if (retry && isValidDescriptor(retry.descriptor)) return retry;
      }
    }
  }
  return null;
}

export type FaceGuide = 'idle' | 'searching' | 'far' | 'near' | 'off_center' | 'ok';

/** ガイド状態ごとの案内メッセージ */
const GUIDE_MESSAGE: Record<FaceGuide, string> = {
  idle:       '準備中...',
  searching:  '顔を枠の中に入れてください',
  far:        'もう少し顔を近づけてください',
  near:       '少し顔を離してください',
  off_center: '顔を枠の中央に合わせてください',
  ok:         '位置OK！そのままボタンを押してください',
};

export function useFaceAuth() {
  const videoRef  = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const guideTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const busyRef   = useRef(false); // 登録/認証キャプチャ中はガイドループを止める
  const [status, setStatus]   = useState<'idle' | 'loading' | 'ready' | 'detecting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [guide, setGuide]     = useState<FaceGuide>('idle');

  // ── リアルタイム顔ガイド（近づけて/遠ざけて/中央へ/OK を常時表示） ──
  const stopLiveGuide = useCallback(() => {
    if (guideTimerRef.current) { clearInterval(guideTimerRef.current); guideTimerRef.current = null; }
  }, []);

  const startLiveGuide = useCallback(() => {
    stopLiveGuide();
    guideTimerRef.current = setInterval(async () => {
      const video = videoRef.current;
      if (!video || busyRef.current || video.videoWidth === 0) return;
      if (!faceapi.nets.tinyFaceDetector.isLoaded) return;
      try {
        const canvas = captureFrame(video);
        const det = await faceapi.detectSingleFace(
          canvas, new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.3 })
        );
        if (busyRef.current) return; // キャプチャが割り込んだら破棄
        let g: FaceGuide;
        if (!det) {
          g = 'searching';
        } else {
          const frac = det.box.height / canvas.height;
          const cx = det.box.x + det.box.width / 2;
          const offset = Math.abs(cx - canvas.width / 2) / canvas.width;
          if (frac < 0.33)      g = 'far';
          else if (frac > 0.72) g = 'near';
          else if (offset > 0.22) g = 'off_center';
          else g = 'ok';
        }
        setGuide(g);
        setMessage(GUIDE_MESSAGE[g]);
      } catch { /* 1フレームの失敗は無視 */ }
    }, 450);
  }, [stopLiveGuide]);

  const startCamera = useCallback(async () => {
    setStatus('loading');
    setMessage('カメラ起動中...');
    try {
      // ① カメラ取得（前面優先 → 失敗したら制約なし）
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }
      streamRef.current = stream;

      // ② 150ms 後に DOM アタッチ（Android WebView 対策）
      await new Promise<void>(resolve => setTimeout(resolve, 150));
      if (videoRef.current && streamRef.current) {
        videoRef.current.srcObject = streamRef.current;
        videoRef.current.play().catch(() => {});
      }

      // ③ カメラ起動成功 → すぐ ready にする（モデルと分離）
      setStatus('ready');
      setMessage('AIモデル読み込み中...');

      // ④ モデルをバックグラウンドロード（失敗しても status は ready のまま）
      try {
        await loadModels();
        await warmup(); // 初回認証の待ちを減らすためGPUカーネルを先に温める
        setMessage('顔を枠の中に入れてください');
        startLiveGuide(); // リアルタイムガイド開始（近づけて/遠ざけて等）
      } catch (modelErr) {
        console.error('[FaceAuth] model load failed in startCamera (will retry on register):', modelErr);
        // エラーでも ready のまま。registerFace で再試行する
        setMessage('顔を正面に向けてください');
      }
    } catch (e) {
      // カメラ起動失敗のみここに到達
      console.error('[FaceAuth] startCamera error:', e);
      setStatus('error');
      setMessage('カメラの起動に失敗しました');
    }
  }, []);

  const stopCamera = useCallback(() => {
    stopLiveGuide();
    setGuide('idle');
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setStatus('idle');
  }, [stopLiveGuide]);

  const registerFace = useCallback(async (userId: string, userName: string, userRole?: string, companyId?: string): Promise<boolean> => {
    if (!videoRef.current) return false;
    busyRef.current = true; // ガイドループを一時停止
    setStatus('detecting');

    try {
      // モデル未ロードなら再試行
      if (!faceapi.nets.tinyFaceDetector.isLoaded) {
        setMessage('モデルを読み込んでいます...');
        try {
          await loadModels();
        } catch (e) {
          console.error('[FaceAuth] model reload failed in registerFace:', e);
          setStatus('error');
          const errMsg = e instanceof Error ? e.message : String(e);
          setMessage(`AIモデルの読み込みに失敗しました: ${errMsg}`);
          return false;
        }
      }

      setMessage('顔を検出中...');
      await waitForVideoReady(videoRef.current);

      // 最大3回リトライ
      for (let attempt = 1; attempt <= 3; attempt++) {
        if (attempt > 1) {
          setMessage(`再試行中... (${attempt}/3)`);
          await new Promise(r => setTimeout(r, 500));
        }
        const canvas    = captureFrame(videoRef.current);
        const detection = await detectFace(canvas);

        if (detection) {
          // 特徴ベクトルを AES-GCM で暗号化してから保存
          const encDescriptor = await encryptFeatures(detection.descriptor);
          // 顔認証ログイン用シークレットをサーバから発行（要オンライン・認証済み）
          let faceSecret: string | undefined;
          try {
            setMessage('顔を登録しています...');
            // 特徴量をサーバにも保存（ローカルとサーバ両方に保持）＋シークレット取得
            faceSecret = await faceRegisterSecret(Array.from(detection.descriptor));
          } catch (secErr) {
            console.warn('[FaceAuth] faceRegisterSecret failed (offline?):', secErr);
          }
          const stored: StoredFace[] = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
          const filtered = stored.filter(f => f.userId !== userId);
          // サーバ送信に失敗（オフライン等）した場合は pendingUpload を立て、
          // オンライン復帰時に syncPendingFaceRegistration が自動でサーバへ再送する。
          filtered.push({ userId, userName, encDescriptor, faceSecret, userRole, companyId, ...(faceSecret ? {} : { pendingUpload: true }) });
          localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
          setStatus('success');
          setMessage(faceSecret ? '顔の登録が完了しました！' : '顔を登録しました（オンライン復帰時にサーバへ自動送信されます）');
          return true;
        }
      }

      setStatus('error');
      setMessage('顔が検出できませんでした。明るい場所で正面を向いて再度お試しください。');
      return false;
    } catch (e) {
      console.error('[FaceAuth] registerFace error:', e);
      setStatus('error');
      setMessage('顔の登録に失敗しました');
      return false;
    } finally {
      busyRef.current = false;
    }
  }, []);

  const recognizeFace = useCallback(async (): Promise<StoredFace | null> => {
    if (!videoRef.current) return null;
    busyRef.current = true; // ガイドループを一時停止
    setStatus('detecting');

    try {
      const rawStored: StoredFace[] = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
      if (rawStored.length === 0) {
        setStatus('error');
        setMessage('登録された顔データがありません');
        return null;
      }

      if (!faceapi.nets.tinyFaceDetector.isLoaded) {
        setMessage('モデルを読み込んでいます...');
        try {
          await loadModels();
        } catch (e) {
          console.error('[FaceAuth] model reload failed in recognizeFace:', e);
          setStatus('error');
          const errMsg = e instanceof Error ? e.message : String(e);
          setMessage(`AIモデルの読み込みに失敗しました: ${errMsg}`);
          return null;
        }
      }

      // 各エントリの特徴ベクトルを復号（セッション鍵が異なる場合はスキップ）
      type DecryptedFace = { userId: string; userName: string; descriptor: Float32Array; faceSecret?: string; userRole?: string; companyId?: string };
      const decrypted: DecryptedFace[] = [];
      for (const raw of rawStored) {
        try {
          if (raw.encDescriptor) {
            // 新形式: AES-GCM 復号
            const desc = await decryptFeatures(raw.encDescriptor);
            decrypted.push({ userId: raw.userId, userName: raw.userName, descriptor: desc, faceSecret: raw.faceSecret, userRole: raw.userRole, companyId: raw.companyId });
          } else if (raw.descriptor) {
            // 旧形式（平文）の後方互換
            decrypted.push({ userId: raw.userId, userName: raw.userName, descriptor: new Float32Array(raw.descriptor), faceSecret: raw.faceSecret, userRole: raw.userRole, companyId: raw.companyId });
          }
        } catch {
          // 復号失敗（セッション鍵の不一致等）はスキップ
        }
      }
      if (decrypted.length === 0) {
        setStatus('error');
        setMessage('顔データの読み込みに失敗しました。再登録してください。');
        return null;
      }

      setMessage('顔を認識中...');
      await waitForVideoReady(videoRef.current);

      for (let attempt = 1; attempt <= 2; attempt++) {
        if (attempt > 1) await new Promise(r => setTimeout(r, 250));
        const canvas    = captureFrame(videoRef.current);
        const detection = await detectFace(canvas);

        if (detection) {
          const labeledDescriptors = decrypted.map(f =>
            new faceapi.LabeledFaceDescriptors(f.userId, [f.descriptor])
          );
          const matcher = new faceapi.FaceMatcher(labeledDescriptors, 0.5);
          const match   = matcher.findBestMatch(detection.descriptor);

          if (match.label !== 'unknown') {
            const found = decrypted.find(f => f.userId === match.label) ?? null;
            if (found) {
              setStatus('success');
              setMessage(found.userName + ' さんを認識しました');
            }
            // StoredFace 互換形式で返す（faceSecret/役割/会社を含めてログインに使う）
            return found ? { userId: found.userId, userName: found.userName, faceSecret: found.faceSecret, userRole: found.userRole, companyId: found.companyId } : null;
          }
        }
      }

      setStatus('error');
      setMessage('顔が検出できませんでした');
      return null;
    } catch (e) {
      console.error('[FaceAuth] recognizeFace error:', e);
      setStatus('error');
      setMessage('顔認証に失敗しました');
      return null;
    } finally {
      busyRef.current = false;
    }
  }, []);

  return { videoRef, status, message, guide, startCamera, stopCamera, registerFace, recognizeFace };
}
